import React, { useState, useEffect } from 'react';
import { Play, Video, Eye, RefreshCw, Check, Clock, Film, Sparkles, Download, Users, Archive, Calendar, Plus, Send, PenTool } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VideoSeriesManager() {
  const [episodes, setEpisodes] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const [previewEpisode, setPreviewEpisode] = useState(null);
  const [generatingMonth, setGeneratingMonth] = useState(false);
  
  // Custom topic input
  const [customTopic, setCustomTopic] = useState('');
  const [generatingCustom, setGeneratingCustom] = useState(false);

  useEffect(() => {
    loadEpisodes();
    loadCharacters();
  }, []);

  const loadEpisodes = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/video-series/gli-occhi-parlanti`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEpisodes(res.data.episodes || []);
    } catch (error) {
      console.error('Error loading episodes:', error);
      toast.error('Errore nel caricamento degli episodi');
    } finally {
      setLoading(false);
    }
  };

  const loadCharacters = async () => {
    try {
      const res = await axios.get(`${API}/video-series/characters`);
      setCharacters(res.data.characters || []);
    } catch (error) {
      console.error('Error loading characters:', error);
    }
  };

  const generateVideo = async (episodeId) => {
    const token = localStorage.getItem('token');
    setGenerating(prev => ({ ...prev, [episodeId]: true }));
    
    try {
      toast.info('🎬 Generazione video HeyGen in corso... (3-10 minuti)', { duration: 10000 });
      
      const res = await axios.post(
        `${API}/video-series/generate/${episodeId}`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 900000
        }
      );
      
      if (res.data.success) {
        toast.success('✅ Video generato con successo!');
        loadEpisodes();
      } else {
        toast.error(res.data.error || 'Errore nella generazione');
      }
    } catch (error) {
      console.error('Error generating video:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Errore nella generazione del video';
      toast.error(`❌ ${errorMsg}`);
    } finally {
      setGenerating(prev => ({ ...prev, [episodeId]: false }));
    }
  };

  const regenerateVideo = async (episodeId) => {
    const token = localStorage.getItem('token');
    
    if (!window.confirm('Sei sicuro di voler rigenerare questo video? Il video attuale verrà eliminato.')) {
      return;
    }
    
    setGenerating(prev => ({ ...prev, [episodeId]: true }));
    
    try {
      toast.info('🔄 Rigenerazione video in corso... (3-10 minuti)', { duration: 10000 });
      
      const res = await axios.post(
        `${API}/video-series/regenerate/${episodeId}`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 900000
        }
      );
      
      if (res.data.success) {
        toast.success('✅ Video rigenerato con successo!');
        loadEpisodes();
      } else {
        toast.error(res.data.error || 'Errore nella rigenerazione');
      }
    } catch (error) {
      console.error('Error regenerating video:', error);
      toast.error(`❌ ${error.response?.data?.error || 'Errore nella rigenerazione'}`);
    } finally {
      setGenerating(prev => ({ ...prev, [episodeId]: false }));
    }
  };

  const archiveAndRegenerate = async (episodeId) => {
    const token = localStorage.getItem('token');
    
    if (!window.confirm('Archiviare questo episodio e generare un NUOVO script con dialogo diverso?')) {
      return;
    }
    
    setGenerating(prev => ({ ...prev, [episodeId]: true }));
    
    try {
      toast.info('📦 Archiviazione e generazione nuovo script...', { duration: 5000 });
      
      const res = await axios.post(
        `${API}/video-series/archive/${episodeId}`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        }
      );
      
      if (res.data.success) {
        toast.success('✅ Archiviato! Nuovo script generato con dialogo diverso');
        loadEpisodes();
      } else {
        toast.error(res.data.error || 'Errore nell\'archiviazione');
      }
    } catch (error) {
      console.error('Error archiving:', error);
      toast.error(`❌ ${error.response?.data?.error || 'Errore nell\'archiviazione'}`);
    } finally {
      setGenerating(prev => ({ ...prev, [episodeId]: false }));
    }
  };

  // Genera contenuti per tutto il mese (4 sabati)
  const generateMonthContent = async () => {
    const token = localStorage.getItem('token');
    
    if (!window.confirm('Generare script per i prossimi 4 sabati? Gli argomenti saranno unici e non ripetuti.')) {
      return;
    }
    
    setGeneratingMonth(true);
    
    try {
      toast.info('📅 Generazione contenuti per il mese in corso...', { duration: 10000 });
      
      const res = await axios.post(
        `${API}/video-series/generate-month`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000
        }
      );
      
      if (res.data.success) {
        toast.success(`✅ Generati ${res.data.episodes_created} nuovi episodi!`);
        loadEpisodes();
      } else {
        toast.error(res.data.error || 'Errore nella generazione');
      }
    } catch (error) {
      console.error('Error generating month content:', error);
      toast.error(`❌ ${error.response?.data?.error || 'Errore nella generazione mensile'}`);
    } finally {
      setGeneratingMonth(false);
    }
  };

  // Genera da argomento custom
  const generateFromCustomTopic = async () => {
    if (!customTopic.trim()) {
      toast.error('Inserisci un argomento!');
      return;
    }
    
    const token = localStorage.getItem('token');
    setGeneratingCustom(true);
    
    try {
      toast.info('✨ Generazione script per: ' + customTopic, { duration: 5000 });
      
      const res = await axios.post(
        `${API}/video-series/generate-from-topic`,
        { topic: customTopic },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        }
      );
      
      if (res.data.success) {
        toast.success('✅ Episodio creato! Puoi generare il video.');
        setCustomTopic('');
        loadEpisodes();
      } else {
        toast.error(res.data.error || 'Errore nella generazione');
      }
    } catch (error) {
      console.error('Error generating from topic:', error);
      toast.error(`❌ ${error.response?.data?.error || 'Errore nella generazione'}`);
    } finally {
      setGeneratingCustom(false);
    }
  };

  // Pubblica subito un video
  const publishNow = async (episodeId) => {
    const token = localStorage.getItem('token');
    
    if (!window.confirm('Pubblicare questo video su Instagram ORA?')) {
      return;
    }
    
    setGenerating(prev => ({ ...prev, [episodeId]: true }));
    
    try {
      toast.info('📤 Pubblicazione in corso...', { duration: 5000 });
      
      const res = await axios.post(
        `${API}/video-series/publish-now/${episodeId}`,
        {},
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000
        }
      );
      
      if (res.data.success) {
        toast.success('✅ Video pubblicato su Instagram!');
        loadEpisodes();
      } else {
        toast.error(res.data.error || 'Errore nella pubblicazione');
      }
    } catch (error) {
      console.error('Error publishing:', error);
      toast.error(`❌ ${error.response?.data?.error || 'Errore nella pubblicazione'}`);
    } finally {
      setGenerating(prev => ({ ...prev, [episodeId]: false }));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'script_ready': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Script Pronto' },
      'generating': { bg: 'bg-blue-100', text: 'text-blue-700', label: '⏳ In Generazione' },
      'ready': { bg: 'bg-[#E8F4F8]', text: 'text-[#2A9FCB]', label: '✅ Video Pronto' },
      'failed': { bg: 'bg-red-100', text: 'text-red-700', label: '❌ Errore' },
      'published': { bg: 'bg-purple-100', text: 'text-purple-700', label: '📸 Pubblicato' }
    };
    const badge = badges[status] || badges['script_ready'];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  // Calcola la data del prossimo sabato
  const getNextSaturday = (weeksAhead = 0) => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSaturday = (6 - dayOfWeek + 7) % 7 || 7;
    const nextSaturday = new Date(today);
    nextSaturday.setDate(today.getDate() + daysUntilSaturday + (weeksAhead * 7));
    return nextSaturday.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#2A9FCB]" />
      </div>
    );
  }

  const readyEpisodes = episodes.filter(e => e.status === 'ready' && !e.published_to_instagram);
  const publishedEpisodes = episodes.filter(e => e.published_to_instagram);
  const pendingEpisodes = episodes.filter(e => e.status === 'script_ready' || e.status === 'generating' || e.status === 'failed');

  return (
    <div className="space-y-6" data-testid="video-series-manager">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2A9FCB] to-[#1a7fa8] rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Film className="w-8 h-8" />
          <h2 className="text-2xl font-bold">Gli Occhi Parlanti - Prime Vision</h2>
        </div>
        <p className="text-white/80">
          Serie di video educativi sulle patologie oculari • Generati con HeyGen AI
        </p>
        <p className="text-white/60 text-sm mt-1">
          📅 Pubblicazione automatica: ogni Sabato alle 10:00 su Instagram
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Genera Mese */}
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-purple-500">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-500" />
            Genera Contenuti Mese
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Crea automaticamente 4 episodi per i prossimi 4 sabati con argomenti unici
          </p>
          <button
            onClick={generateMonthContent}
            disabled={generatingMonth}
            className="w-full px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {generatingMonth ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Generazione in corso...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Genera 4 Episodi per il Mese
              </>
            )}
          </button>
        </div>

        {/* Genera da Argomento Custom */}
        <div className="bg-white rounded-xl shadow-sm p-4 border-l-4 border-teal-500">
          <h3 className="font-semibold mb-2 flex items-center gap-2">
            <PenTool className="w-5 h-5 text-teal-500" />
            Crea da Argomento Specifico
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Descrivi un argomento e GPT genererà lo script
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={customTopic}
              onChange={(e) => setCustomTopic(e.target.value)}
              placeholder="Es: Congiuntivite allergica, Lenti progressive..."
              className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:outline-none text-sm"
            />
            <button
              onClick={generateFromCustomTopic}
              disabled={generatingCustom || !customTopic.trim()}
              className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50"
            >
              {generatingCustom ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Programmazione Prossimi Sabati */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#2A9FCB]" />
          Programmazione Prossimi Sabati
        </h3>
        <div className="grid grid-cols-4 gap-2">
          {[0, 1, 2, 3].map((weekOffset) => {
            const scheduledEpisode = readyEpisodes[weekOffset];
            return (
              <div key={weekOffset} className={`p-3 rounded-lg text-center ${scheduledEpisode ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                <p className="text-xs font-medium text-gray-500">{getNextSaturday(weekOffset)}</p>
                {scheduledEpisode ? (
                  <p className="text-sm font-semibold text-[#2A9FCB] truncate mt-1" title={scheduledEpisode.title}>
                    {scheduledEpisode.title}
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 mt-1">Nessun video</p>
                )}
              </div>
            );
          })}
        </div>
        {readyEpisodes.length < 4 && (
          <p className="text-xs text-orange-600 mt-2">
            ⚠️ Hai solo {readyEpisodes.length} video pronti. Genera più contenuti per coprire tutto il mese!
          </p>
        )}
      </div>

      {/* Characters Preview */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-[#2A9FCB]" />
          I Protagonisti ({characters.length})
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {characters.map((char) => (
            <div key={char.id} className="flex flex-col items-center p-4 bg-gradient-to-b from-gray-50 to-white rounded-lg border hover:shadow-md transition-all">
              <img 
                src={`${API}/static/characters/${char.id === 'lucia' ? 'lucia_umana' : char.id === 'dr_visione' ? 'dr_visione' : char.id === 'marco' ? 'marco_bambino' : 'nonna_rosa'}.png`} 
                alt={char.name} 
                className="w-20 h-20 object-contain rounded-full bg-white shadow-sm"
                onError={(e) => {
                  e.target.src = `${API}/static/characters/${char.id}.png`;
                }}
              />
              <p className="font-bold text-sm mt-2">{char.name}</p>
              <p className="text-xs text-gray-500">{char.role}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Video Pronti per Pubblicazione */}
      {readyEpisodes.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2 text-[#2A9FCB]">
            <Check className="w-5 h-5" />
            Video Pronti ({readyEpisodes.length}) - In coda per pubblicazione
          </h3>
          <div className="space-y-3">
            {readyEpisodes.map((ep, index) => (
              <div key={ep.id} className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <span className="bg-[#2A9FCB] text-white text-xs font-bold px-2 py-1 rounded">
                    #{index + 1}
                  </span>
                  <div>
                    <p className="font-semibold">{ep.title}</p>
                    <p className="text-xs text-gray-500">Pubblica: {getNextSaturday(index)}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPreviewEpisode(ep)}
                    className="px-3 py-1 bg-[#2A9FCB] text-white rounded text-sm hover:bg-[#1e7ba8]"
                  >
                    <Play className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => publishNow(ep.id)}
                    disabled={generating[ep.id]}
                    className="px-3 py-1 bg-pink-500 text-white rounded text-sm hover:bg-pink-600 flex items-center gap-1"
                  >
                    <Send className="w-4 h-4" />
                    Pubblica Ora
                  </button>
                  <button
                    onClick={() => regenerateVideo(ep.id)}
                    disabled={generating[ep.id]}
                    className="px-3 py-1 bg-orange-500 text-white rounded text-sm hover:bg-orange-600"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Episodi da Generare */}
      {pendingEpisodes.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2 text-yellow-700">
            <Clock className="w-5 h-5" />
            Episodi da Generare ({pendingEpisodes.length})
          </h3>
          <div className="space-y-3">
            {pendingEpisodes.map((ep) => (
              <div key={ep.id} className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold">{ep.title}</p>
                    {getStatusBadge(ep.status)}
                  </div>
                  <p className="text-xs text-gray-500 italic">"{ep.script?.lucia_symptoms?.substring(0, 60)}..."</p>
                  {ep.error && <p className="text-xs text-red-500 mt-1">❌ {ep.error}</p>}
                </div>
                <button
                  onClick={() => generateVideo(ep.id)}
                  disabled={generating[ep.id] || ep.status === 'generating'}
                  className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 flex items-center gap-2"
                >
                  {generating[ep.id] || ep.status === 'generating' ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Genera Video
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Archivio Pubblicati */}
      {publishedEpisodes.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2 text-purple-700">
            <Archive className="w-5 h-5" />
            Archivio Pubblicati ({publishedEpisodes.length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {publishedEpisodes.map((ep) => (
              <div key={ep.id} className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center">
                <p className="font-medium text-sm truncate">{ep.title}</p>
                <p className="text-xs text-purple-500">
                  {ep.instagram_published_at ? new Date(ep.instagram_published_at).toLocaleDateString('it-IT') : 'Pubblicato'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Video Preview Modal */}
      {previewEpisode && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">{previewEpisode.title}</h3>
              <button
                onClick={() => setPreviewEpisode(null)}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              <video
                src={`${API.replace('/api', '')}${previewEpisode.video_url}`}
                controls
                autoPlay
                className="w-full rounded-lg"
                style={{ maxHeight: '70vh' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h4 className="font-semibold text-blue-800 mb-2">ℹ️ Come funziona</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Genera Mese:</strong> Crea 4 episodi automatici per i prossimi 4 sabati</li>
          <li>• <strong>Argomento Custom:</strong> Descrivi un tema e GPT crea lo script</li>
          <li>• <strong>Genera Video:</strong> Crea il video con HeyGen (3-10 minuti)</li>
          <li>• <strong>Pubblica Ora:</strong> Pubblica immediatamente su Instagram</li>
          <li>• 🔄 <strong>Rigenera:</strong> Non ti piace? Rigenera lo stesso episodio</li>
          <li>• 📅 <strong>Automatico:</strong> Ogni sabato alle 10:00 viene pubblicato il primo in coda</li>
        </ul>
      </div>
    </div>
  );
}
